import React, { useEffect, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import './ColumnSelector.css';

/**
 * ============================================================================
 * COLUMN SELECTOR — the "Manage Columns" toolbar control every list screen carries
 * ============================================================================
 * Lifted from SampleMasterScreen so relocatable modules (the tax-master module first)
 * can use it without copying 130 lines. Render it through the screen shell's
 * `customToolbarItems`; keep `'actions'` (and `'select'` when row selection is on) as
 * `required: true` rows so the row-action menu can never be hidden (workspace rule
 * "SyutGrid row-actions visibility").
 *
 * Persist the applied set per screen in localStorage — the caller owns the key.
 */

export interface ColumnDefinition {
  id: string;
  label: string;
  /** Kept for callers that map definitions onto grid columns; not used here. */
  field?: string;
  /** Cannot be hidden (structural: select / actions). */
  required?: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

export interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
  /** Tooltip / heading. */
  title?: string;
}

export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns, visibleColumns: initialVisible, onApply, onReset, title = 'Manage Columns',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => { setSelectedColumns(initialVisible); }, [initialVisible]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]);
  };

  const handleSelectAll = () => {
    const optional = columns.filter((col) => !col.required).map((col) => col.id);
    const required = columns.filter((col) => col.required).map((col) => col.id);
    if (selectedColumns.length === columns.length) setSelectedColumns(required);
    else setSelectedColumns([...required, ...optional]);
  };

  const handleApply = () => { onApply(selectedColumns); setIsOpen(false); };
  const handleCancel = () => { setSelectedColumns(initialVisible); setIsOpen(false); };

  return (
    <div className="column-selector" ref={panelRef}>
      <button
        type="button"
        className={`btn-icon${isOpen ? ' active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title={title}
        aria-expanded={isOpen}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {isMobile && <div className="column-selector-backdrop" onClick={() => setIsOpen(false)} />}
          <div className={`column-selector-panel${isMobile ? ' is-mobile' : ''}`}>
            <div className="column-selector-header">
              <h3 className="column-selector-title">{title}</h3>
              <button type="button" className="column-selector-close" onClick={() => setIsOpen(false)} aria-label="Close">
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="column-selector-list">
              {columns.map((column) => {
                const checked = selectedColumns.includes(column.id);
                return (
                  <div
                    key={column.id}
                    className={`column-selector-item${column.required ? ' is-required' : ''}`}
                    onClick={() => toggleColumn(column.id)}
                    role="checkbox"
                    aria-checked={checked}
                    tabIndex={column.required ? -1 : 0}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleColumn(column.id); } }}
                  >
                    <span className={`column-selector-check${checked ? ' is-checked' : ''}`}>
                      {checked && <IconSystem.check size={12} />}
                    </span>
                    <span className="column-selector-label">{column.label}{column.required && ' *'}</span>
                  </div>
                );
              })}
            </div>
            <div className="column-selector-footer">
              <div className="column-selector-footer-row">
                <button type="button" className="btn btn-secondary" onClick={handleSelectAll}>
                  {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setConfirmResetOpen(true)}>
                  Reset
                </button>
              </div>
              <div className="column-selector-footer-row is-end">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleApply}>Apply</button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to the default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

export default ColumnSelector;
